import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, Bell, Check, Smartphone, Share, MoreVertical, Sparkles, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Confetti from './Confetti';
import { useAuth } from '../contexts/AuthContext';
import { requestOnboardingSlot, releaseOnboardingSlot } from '../onboardingQueue';
import { needsRealEmail } from '../utils/needsRealEmail';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const SESSION_DISMISS_KEY = 'install_bonus_dismissed_session';
const PERSISTENT_DISMISS_KEY = 'install_bonus_dismissed_permanent';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
}

function detectPlatform() {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
    return { isIOS, isAndroid, isStandalone };
}

const InstallBonusModal = () => {
    const { user, refreshUser } = useAuth();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [platform] = useState(detectPlatform());
    const [busy, setBusy] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [iosExpanded, setIosExpanded] = useState(false);
    const mounted = useRef(false);

    // Capture the browser's install prompt (Chrome/Edge/Android)
    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    // Fetch bonus status when user logs in
    const fetchStatus = useCallback(async () => {
        if (!user || user.role !== 'user') return;
        try {
            const { data } = await axios.get(`${API}/rewards/onboarding-status`);
            setStatus(data);
            const dismissedSession = sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
            // Permanent skip — once a player taps Skip we never auto-pop again.
            // They can still claim the bonuses on the Rewards page anytime.
            const dismissedForever = localStorage.getItem(PERSISTENT_DISMISS_KEY) === '1';
            if (!dismissedSession && !dismissedForever && !data.is_admin && (!data.install_claimed || !data.push_claimed)) {
                // Queue behind any other onboarding modal so they never
                // stack. Mark seen only once we're actually granted the
                // slot (not while merely waiting) so a modal that never
                // gets its turn still shows next session.
                requestOnboardingSlot('install-bonus', 30, () => {
                    try { localStorage.setItem(PERSISTENT_DISMISS_KEY, '1'); } catch { /* ignore quota */ }
                    setOpen(true);
                });
            }
        } catch (e) {
            console.warn('Failed to load onboarding status', e);
        }
    }, [user]);

    useEffect(() => {
        if (mounted.current) return;
        if (user && user.role === 'user') {
            mounted.current = true;
            fetchStatus();
        }
    }, [user, fetchStatus]);

    // Release the queue slot on unmount so we never deadlock it.
    useEffect(() => () => releaseOnboardingSlot('install-bonus'), []);

    // If already running as installed PWA and install bonus still unclaimed → auto-claim
    useEffect(() => {
        if (!status || status.install_claimed || !platform.isStandalone) return;
        claimInstall(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, platform.isStandalone]);

    // Listen for 'appinstalled' event
    useEffect(() => {
        const handler = () => {
            if (status && !status.install_claimed) claimInstall(true);
        };
        window.addEventListener('appinstalled', handler);
        return () => window.removeEventListener('appinstalled', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const burstConfetti = () => {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3500);
    };

    const claimInstall = async (silent = false) => {
        if (busy || (status && status.install_claimed)) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/rewards/claim-install-bonus`);
            setStatus((s) => ({ ...s, install_claimed: true }));
            if (refreshUser) await refreshUser();
            burstConfetti();
            toast.success(data.message || '$5 bonus credited!', {
                description: 'Welcome to the home-screen club!',
            });
        } catch (e) {
            if (!silent) {
                const msg = e.response?.data?.detail || 'Could not claim install bonus';
                toast.error(typeof msg === 'string' ? msg : 'Could not claim install bonus');
            }
        } finally {
            setBusy(false);
        }
    };

    const handleInstallClick = async () => {
        // Android / Chrome / Edge path
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            setDeferredPrompt(null);
            if (choice?.outcome === 'accepted') {
                // appinstalled event will fire shortly and claim. Fallback after 3s:
                setTimeout(() => {
                    if (status && !status.install_claimed) claimInstall(true);
                }, 3000);
            }
            return;
        }
        // iOS or already-installed case → expand instructions
        if (platform.isIOS) {
            setIosExpanded(true);
            return;
        }
        if (platform.isStandalone) {
            claimInstall();
            return;
        }
        // Desktop browsers without install prompt — show instructions toast
        toast.info('Open this site in Chrome/Edge/Safari on your phone to install.', {
            description: 'Then come back here to claim your $5.',
        });
    };

    const handleIosConfirm = () => {
        // Trust-based claim for iOS users who follow the Share → Add to Home Screen flow
        claimInstall();
        setIosExpanded(false);
    };

    const enablePush = async () => {
        if (busy || (status && status.push_claimed)) return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            toast.error('Push notifications not supported on this browser.');
            return;
        }
        setBusy(true);
        try {
            const perm = await Notification.requestPermission();
            if (perm !== 'granted') {
                toast.error('Notifications blocked', {
                    description: 'Enable notifications in your browser settings and try again.',
                });
                setBusy(false);
                return;
            }
            const reg = await navigator.serviceWorker.ready;
            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
                });
            }
            const subJson = sub.toJSON();
            await axios.post(`${API}/notifications/subscribe`, {
                endpoint: subJson.endpoint,
                keys: subJson.keys,
            });
            const { data } = await axios.post(`${API}/rewards/claim-push-bonus`);
            setStatus((s) => ({ ...s, push_claimed: true }));
            if (refreshUser) await refreshUser();
            burstConfetti();
            toast.success(data.message || '$5 bonus credited!', {
                description: "We'll ping you when jackpots go hot!",
            });
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || 'Could not enable push notifications';
            toast.error(typeof msg === 'string' ? msg : 'Could not enable notifications');
        } finally {
            setBusy(false);
        }
    };

    // Auto-close once both claimed
    useEffect(() => {
        if (status && status.install_claimed && status.push_claimed && open) {
            const t = setTimeout(() => { setOpen(false); releaseOnboardingSlot('install-bonus'); }, 1800);
            return () => clearTimeout(t);
        }
    }, [status, open]);

    const dismiss = () => {
        // Permanent: never auto-show this modal again on this device.
        // Bonuses stay claimable on the Rewards page.
        try {
            localStorage.setItem(PERSISTENT_DISMISS_KEY, '1');
        } catch { /* ignore quota */ }
        sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
        setOpen(false);
        releaseOnboardingSlot('install-bonus');
    };

    if (!user || !['user'].includes(user.role) || !status) return null;
    // Forced-email gate owns the screen — don't pop over it.
    if (needsRealEmail(user)) return null;
    // Never cover a real-money game screen (/casino/<game>) — it blocks SPIN.
    // Bonuses stay claimable on the Rewards page and the modal shows elsewhere.
    if (/^\/casino\/.+/.test(window.location.pathname)) return null;
    if (status.install_claimed && status.push_claimed && !open) return null;

    const totalBonus = (status.install_bonus || 5) + (status.push_bonus || 5);
    const claimed =
        (status.install_claimed ? status.install_bonus || 5 : 0) +
        (status.push_claimed ? status.push_bonus || 5 : 0);
    // First-load-match gate — install + push cards stay locked until the
    // player has crossed the $10 real-money threshold. This is the Feb 2026
    // promo overhaul: no more freeloading the $10 in bonuses.
    const matchClaimed = !!status.first_load_match_claimed;
    const matchAmount = status.first_load_match_amount || 10;
    const matchMin = status.first_load_match_min || 10;
    const lockedReason = matchClaimed ? null : `Load $${matchMin}+ to unlock`;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
                    data-testid="install-bonus-modal"
                >
                    {showConfetti && <Confetti />}
                    <motion.div
                        initial={{ scale: 0.85, y: 40, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: 'spring', damping: 22, stiffness: 260 }}
                        className="relative w-full max-w-md rounded-3xl overflow-hidden"
                        style={{
                            background:
                                'linear-gradient(145deg, #1a0f02 0%, #0a0a0a 60%, #120900 100%)',
                            border: '1px solid rgba(255, 215, 0, 0.35)',
                            boxShadow:
                                '0 0 0 1px rgba(255,215,0,0.15), 0 30px 80px -10px rgba(255,215,0,0.25), inset 0 0 60px rgba(255,215,0,0.08)',
                        }}
                    >
                        {/* Gold shimmer overlay */}
                        <div
                            className="absolute inset-0 pointer-events-none opacity-40"
                            style={{
                                background:
                                    'radial-gradient(120% 60% at 50% 0%, rgba(255,215,0,0.25) 0%, transparent 60%)',
                            }}
                        />
                        {/* Shimmer bar */}
                        <motion.div
                            className="absolute top-0 left-0 h-0.5 w-full"
                            style={{
                                background:
                                    'linear-gradient(90deg, transparent, #FFD700, #FFF4A3, #FFD700, transparent)',
                            }}
                            animate={{ x: ['-100%', '100%'] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                        />

                        <button
                            onClick={dismiss}
                            className="absolute top-3 right-3 z-10 p-2 rounded-full text-[#FFD700]/60 hover:text-white hover:bg-white/10 transition"
                            data-testid="install-bonus-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="relative px-6 pt-8 pb-6 text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.2, 1] }}
                                transition={{ delay: 0.15, duration: 0.55 }}
                                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3"
                                style={{
                                    background:
                                        'radial-gradient(circle, #FFD700 0%, #B8860B 70%, transparent 100%)',
                                    boxShadow: '0 0 40px rgba(255,215,0,0.5)',
                                }}
                            >
                                <Gift className="w-8 h-8 text-[#0a0a0a]" strokeWidth={2.6} />
                            </motion.div>
                            <h2
                                className="text-3xl sm:text-4xl font-black tracking-tight"
                                style={{
                                    background:
                                        'linear-gradient(180deg, #FFF4A3 0%, #FFD700 45%, #B8860B 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    textShadow: '0 2px 20px rgba(255,215,0,0.3)',
                                }}
                                data-testid="install-bonus-title"
                            >
                                Unlock ${totalBonus} in Bonus Credits
                            </h2>
                            <p className="text-[#A0AAB2] text-sm mt-2 font-medium">
                                {matchClaimed
                                    ? "Two quick steps. One-time rewards. Let's go!"
                                    : `First, load $${matchMin} → we match $${matchAmount}. Then unlock $${totalBonus} more below.`}
                            </p>
                            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30">
                                <Sparkles className="w-3.5 h-3.5 text-[#FFD700]" />
                                <span className="text-xs font-bold text-[#FFD700]">
                                    ${claimed} of ${totalBonus} claimed
                                </span>
                            </div>
                        </div>

                        <div className="px-6 pb-6 space-y-3">
                            {/* Card 0: First-load $10 match — the new-player skin-in-game gate */}
                            {!matchClaimed && (
                                <div
                                    className="relative rounded-2xl p-4 flex items-center gap-3"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(57,255,20,0.18), rgba(57,255,20,0.03))',
                                        border: '1px solid rgba(57,255,20,0.55)',
                                    }}
                                    data-testid="first-load-match-card"
                                >
                                    <div
                                        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-[#062d00]"
                                        style={{
                                            background: '#39FF14',
                                            boxShadow: '0 0 20px rgba(57,255,20,0.5)',
                                        }}
                                    >
                                        <Gift className="w-5 h-5" strokeWidth={2.6} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-black text-white text-[15px] leading-tight">
                                            Load ${matchMin} · We match ${matchAmount}
                                        </div>
                                        <div className="text-[11.5px] text-[#c9e5d4] mt-0.5 leading-snug">
                                            First load only. Match is bonus credits, 1× wagering. Auto-credited when your load posts.
                                        </div>
                                    </div>
                                    <span
                                        className="shrink-0 text-[11px] font-black tracking-wide px-2.5 py-1 rounded-full"
                                        style={{
                                            background: 'rgba(57,255,20,0.15)',
                                            color: '#39FF14',
                                            border: '1px solid rgba(57,255,20,0.4)',
                                        }}
                                    >
                                        +${matchAmount}
                                    </span>
                                </div>
                            )}
                            {/* Card 1: Install to Home Screen */}
                            <RewardCard
                                claimed={status.install_claimed}
                                locked={!matchClaimed}
                                lockedReason={lockedReason}
                                icon={<Home className="w-5 h-5" />}
                                title="Add to Home Screen"
                                subtitle={`Install the app like a real native app — $${status.install_bonus || 5} instant bonus`}
                                onClick={handleInstallClick}
                                busy={busy}
                                testId="claim-install-btn"
                                actionLabel={
                                    platform.isIOS
                                        ? 'Show iPhone Steps'
                                        : deferredPrompt
                                        ? 'Install Now'
                                        : platform.isStandalone
                                        ? 'Claim Now'
                                        : 'How?'
                                }
                            />

                            {/* iOS Instruction Panel */}
                            <AnimatePresence>
                                {iosExpanded && !status.install_claimed && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div
                                            className="mt-1 rounded-xl p-4 text-[13px] leading-relaxed text-[#E0E0E0]"
                                            style={{
                                                background: 'rgba(255,215,0,0.05)',
                                                border: '1px dashed rgba(255,215,0,0.3)',
                                            }}
                                        >
                                            <div className="flex items-center gap-2 mb-2 text-[#FFD700] font-bold">
                                                <Smartphone className="w-4 h-4" /> iPhone / iPad (Safari)
                                            </div>
                                            <ol className="list-decimal list-inside space-y-1.5 text-[#C9D0D6]">
                                                <li className="flex items-start gap-2">
                                                    <span className="flex-1">
                                                        Tap the <Share className="inline w-3.5 h-3.5 mx-0.5 text-[#FFD700]" /> <b>Share</b> icon at the bottom of Safari.
                                                    </span>
                                                </li>
                                                <li>Scroll and tap <b className="text-white">"Add to Home Screen"</b>.</li>
                                                <li>Tap <b className="text-white">Add</b> in the top right.</li>
                                                <li>Open the app from your Home Screen to collect your $5.</li>
                                            </ol>
                                            <button
                                                onClick={handleIosConfirm}
                                                disabled={busy}
                                                className="mt-3 w-full py-2.5 rounded-lg font-bold text-sm bg-[#FFD700] text-black hover:bg-[#FFE55C] transition disabled:opacity-50"
                                                data-testid="ios-install-confirm-btn"
                                            >
                                                {busy ? 'Claiming…' : "I've added it — Claim $5"}
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Android non-prompt fallback hint */}
                            {!platform.isIOS && !platform.isStandalone && !deferredPrompt && !status.install_claimed && (
                                <div
                                    className="rounded-xl p-3 text-[12px] leading-relaxed text-[#A0AAB2]"
                                    style={{
                                        background: 'rgba(255,255,255,0.03)',
                                        border: '1px dashed rgba(255,255,255,0.1)',
                                    }}
                                >
                                    <div className="flex items-center gap-2 mb-1 text-white font-semibold text-[13px]">
                                        <MoreVertical className="w-3.5 h-3.5" /> Android (Chrome)
                                    </div>
                                    Tap the <b>⋮ menu</b> top-right → <b>Install app</b> or <b>Add to Home screen</b>. The $5 lands as soon as you open the installed app.
                                </div>
                            )}

                            {/* Card 2: Push Notifications */}
                            <RewardCard
                                claimed={status.push_claimed}
                                locked={!matchClaimed}
                                lockedReason={lockedReason}
                                icon={<Bell className="w-5 h-5" />}
                                title="Enable Push Notifications"
                                subtitle={`Hot jackpots, bonuses & wins sent straight to you — $${status.push_bonus || 5} instant bonus`}
                                onClick={enablePush}
                                busy={busy}
                                testId="claim-push-btn"
                                actionLabel={'Turn On'}
                            />

                            <button
                                onClick={dismiss}
                                className="w-full mt-2 py-3 rounded-xl font-bold text-sm text-white bg-white/10 hover:bg-white/15 active:bg-white/20 border border-white/15 transition flex items-center justify-center gap-1.5"
                                data-testid="install-bonus-skip"
                                aria-label="Skip and continue to the games"
                            >
                                Skip — take me to the games
                                <span aria-hidden="true">→</span>
                            </button>
                            <p className="text-[10px] text-center text-[#A0AAB2]/70 mt-1.5 leading-tight">
                                You can still claim these bonuses anytime from the Rewards page.
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const RewardCard = ({ claimed, locked, lockedReason, icon, title, subtitle, onClick, busy, actionLabel, testId }) => (
    <motion.div
        whileHover={!claimed && !locked ? { scale: 1.015 } : undefined}
        className="relative rounded-2xl p-4 flex items-center gap-3 transition"
        style={{
            background: claimed
                ? 'linear-gradient(135deg, rgba(57,255,20,0.12), rgba(57,255,20,0.04))'
                : locked
                ? 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(0,0,0,0))'
                : 'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,215,0,0.03))',
            border: claimed
                ? '1px solid rgba(57,255,20,0.5)'
                : locked
                ? '1px dashed rgba(255,255,255,0.18)'
                : '1px solid rgba(255,215,0,0.35)',
            opacity: locked ? 0.75 : 1,
        }}
    >
        <div
            className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
                background: claimed ? '#39FF14' : locked ? 'rgba(255,255,255,0.08)' : 'rgba(255,215,0,0.2)',
                color: claimed ? '#062d00' : locked ? '#8891A0' : '#FFD700',
                boxShadow: claimed
                    ? '0 0 20px rgba(57,255,20,0.4)'
                    : locked
                    ? 'none'
                    : '0 0 18px rgba(255,215,0,0.25)',
            }}
        >
            {claimed ? <Check className="w-5 h-5" strokeWidth={3} /> : icon}
        </div>
        <div className="flex-1 min-w-0">
            <div className="font-bold text-white text-[15px] leading-tight">{title}</div>
            <div className="text-[11.5px] text-[#A0AAB2] mt-0.5 leading-snug">{subtitle}</div>
        </div>
        {claimed ? (
            <span
                className="shrink-0 text-[11px] font-black tracking-wide px-2.5 py-1 rounded-full"
                style={{
                    background: 'rgba(57,255,20,0.15)',
                    color: '#39FF14',
                    border: '1px solid rgba(57,255,20,0.4)',
                }}
            >
                CLAIMED
            </span>
        ) : locked ? (
            <span
                className="shrink-0 text-[10px] font-black tracking-wide px-2 py-1 rounded-full text-center leading-tight max-w-[92px]"
                style={{
                    background: 'rgba(255,255,255,0.06)',
                    color: '#8891A0',
                    border: '1px solid rgba(255,255,255,0.15)',
                }}
                data-testid={`${testId}-locked`}
            >
                🔒 {lockedReason || 'Locked'}
            </span>
        ) : (
            <button
                onClick={onClick}
                disabled={busy}
                className="shrink-0 px-4 py-2 rounded-lg text-xs font-black tracking-wide transition disabled:opacity-50"
                style={{
                    background: 'linear-gradient(180deg, #FFE55C 0%, #FFD700 50%, #B8860B 100%)',
                    color: '#0a0a0a',
                    boxShadow: '0 4px 14px rgba(255,215,0,0.35)',
                }}
                data-testid={testId}
            >
                {busy ? '...' : actionLabel}
            </button>
        )}
    </motion.div>
);

export default InstallBonusModal;
